import { createSelector } from 'reselect';
import {
  getSummary,
  getAnalysis,
  getFormatting,
} from '@/pages/CompanyReport/redux/selectors';
import {
  toSignificant,
  toPercentage,
  toCurrencyTruncated,
} from '@/utilities/formatting';
import { differenceInDays, format } from 'date-fns';
import { get } from 'lodash';
import { getAveragesSelector } from '@ducks/averages/selectors';
import { getUTCDate } from '@/utilities/date';

interface PayoutEntity {
  label: 'paid' | 'retained';
  value: number;
}
export interface Dividend {
  score: number;
  marketName: string;
  exchangeCountryISO: string;
  isFund: boolean;
  companyId: string;
  companyName: string;
  uniqueSymbol: string;
  dividendNextDate: number;
  dividendNextDateFormatted: string;
  showUpcomingDividendPayment: boolean;
  companyNamePlural: string;
  EPSFormatted: string;
  dividendNextPayDate: number;
  dividendNextPayDateFormatted: string;
  lastPaymentDividendCurrency: number;
  industryName: string;
  dividendYield: number | null;
  dividendYieldFuture: number | null;
  dividendYieldFuture3Y: number | null;
  keyMetric: string;
  keyMetricTitle: string;
  dividendYieldFormatted: string;
  marketDividendYieldP25Formatted: string;
  marketDividendYieldP75Formatted: string;
  industryDividendYieldFormatted: string;
  dividendYieldFutureFormatted: string;
  dividendYieldFuture3YFormatted: string;
  marketDividendYieldP25: number;
  marketDividendYieldP75: number;
  industryDividendYield: number;
  currentDividendPayout: PayoutEntity[];
  currentDividendPayoutRatio: number;
  currentDividendPayoutRatioFormatted: string;
  futureDividendPayout: PayoutEntity[];
  futureDividendPayoutRatio: number;
  futureDividendPayoutRatioFormatted: string;
  cashDividendPayout: PayoutEntity[];
  cashDividendPayoutRatio: number;
  cashDividendPayoutRatioFormatted: string;
  tradingItemCurrencyISO: string;
  buybackYieldFormatted: string;
  totalShareholderYieldFormatted: string;
  dividendPaymentsGrowthAnnualFormatted: string;
}

interface KeyMetric {
  value: string;
  title: string;
}

const getDividendKeyMetic = (
  dividendYieldFuture: number,
  dividendYield: number
): KeyMetric => {
  let value = '';
  let title = '';
  if (dividendYield) {
    value = toPercentage(dividendYield, 2);
    title = 'Current Dividend Yield';
  } else if (dividendYieldFuture) {
    value = toPercentage(dividendYieldFuture, 2);
    title = 'Forecast Dividend Yield';
  }
  return {
    value,
    title,
  };
};

const toSignificantNumber = (value: number, significance = 1) =>
  Number(toSignificant(value * 100, significance));

const formatter = (value: number | null, significance = 1) =>
  value !== null ? toPercentage(value, significance) : 'n/a';

export const getDividendSectionSelector = createSelector(
  [getAnalysis, getSummary, getAveragesSelector, getFormatting],
  (analysis, summary, averages, formatting): Dividend => {
    const { getIndustryAverage, getMarketAverage } = averages;
    const currencyISO = summary.currencyISO;
    const mergedFutureYield = analysis.legacyChartData.mergedFutureYield;
    const dividendYieldFuture = analysis.dividendYieldFuture;
    const dividendYieldFuture3Y =
      mergedFutureYield[mergedFutureYield.length - 1]?.[1] ??
      dividendYieldFuture;
    const dividendYield = analysis.dividendYield;
    const dividendKeyMetic = getDividendKeyMetic(
      dividendYieldFuture,
      dividendYield
    );
    const dividendNextDate = get(analysis, ['upcomingDividend', 'date'], 0);
    const dividendNextDateFormatted = dividendNextDate
      ? format(getUTCDate(dividendNextDate), 'dd MMM yy')
      : 'n/a';
    const dividendNextPayDate = get(
      analysis,
      ['upcomingDividend', 'payDate'],
      0
    );
    const dividendNextPayDateFormatted = dividendNextPayDate
      ? format(getUTCDate(dividendNextPayDate), 'dd MMM yy')
      : 'n/a';
    const showUpcomingDividendPayment =
      differenceInDays(new Date(dividendNextDate), Date.now()) > 0;

    const marketDividendYieldP25 = getMarketAverage('dividendYieldP25').value;
    const marketDividendYieldP75 = getMarketAverage('dividendYieldP75').value;
    const industryDividendYield = getIndustryAverage('dividendYield').value;

    const dividendYieldFormatted = formatter(dividendYield);
    const marketDividendYieldP25Formatted = formatter(marketDividendYieldP25);
    const marketDividendYieldP75Formatted = formatter(marketDividendYieldP75);
    const industryDividendYieldFormatted = formatter(industryDividendYield);
    const dividendYieldFuture3YFormatted = formatter(dividendYieldFuture3Y);
    const dividendYieldFutureFormatted = formatter(dividendYieldFuture);
    const lastPaymentDividendCurrency = analysis.lastPaymentDividendCurrency;
    const tradingItemCurrencyISO = formatting.tradingItemCurrencyISO;

    const buybackYield = analysis.buybackYield;
    const buybackYieldFormatted = formatter(buybackYield);

    const totalShareholderYield = analysis.totalShareholderYield;
    const totalShareholderYieldFormatted = formatter(totalShareholderYield);

    const dividendPaymentsGrowthAnnual = analysis.dividendPaymentsGrowthAnnual;
    const dividendPaymentsGrowthAnnualFormatted = formatter(
      dividendPaymentsGrowthAnnual
    );

    const payoutRatio = analysis.payoutRatio;
    const currentDividendPayoutRatioFormatted = formatter(payoutRatio, 0);
    const currentDividendPayout: PayoutEntity[] = [
      {
        label: 'paid',
        value: toSignificantNumber(payoutRatio < 0 ? 1 : payoutRatio, 0),
      },
    ];
    if (payoutRatio < 1 && payoutRatio >= 0) {
      currentDividendPayout.push({
        label: 'retained',
        value: toSignificantNumber(1 - payoutRatio, 0),
      });
    }

    const futurePayoutRatio = analysis.payoutRatio3y;
    const futureDividendPayoutRatioFormatted = formatter(futurePayoutRatio, 0);
    const futureDividendPayout: PayoutEntity[] = [
      {
        label: 'paid',
        value: toSignificantNumber(
          futurePayoutRatio < 0 ? 1 : futurePayoutRatio,
          0
        ),
      },
    ];
    if (futurePayoutRatio < 1 && futurePayoutRatio >= 0) {
      futureDividendPayout.push({
        label: 'retained',
        value: toSignificantNumber(1 - futurePayoutRatio, 0),
      });
    }

    const cashPayoutRatio = analysis.cashPayoutRatio;
    const cashDividendPayoutRatioFormatted = formatter(cashPayoutRatio, 0);
    const cashDividendPayout: PayoutEntity[] = [
      {
        label: 'paid',
        value: toSignificantNumber(
          cashPayoutRatio < 0 ? 1 : cashPayoutRatio,
          0
        ),
      },
    ];
    if (cashPayoutRatio < 1 && cashPayoutRatio >= 0) {
      cashDividendPayout.push({
        label: 'retained',
        value: toSignificantNumber(1 - cashPayoutRatio, 0),
      });
    }

    return {
      keyMetric: dividendKeyMetic.value,
      keyMetricTitle: dividendKeyMetic.title,
      score: summary.score.income,
      industryName: summary.industryData.secondaryName,
      isFund: summary.isFund,
      marketName: getMarketAverage('netIncomeGrowthAnnual').source,
      exchangeCountryISO: summary.exchangeCountryISO,
      companyId: summary.companyId,
      companyName: summary.shortName,
      uniqueSymbol: summary.uniqueSymbol,
      EPSFormatted: toCurrencyTruncated(analysis.EPS, 2, currencyISO),
      dividendNextDate,
      dividendNextDateFormatted,
      showUpcomingDividendPayment,
      dividendNextPayDate,
      dividendNextPayDateFormatted,
      companyNamePlural: summary.shortNamePlural,
      dividendYield,
      marketDividendYieldP25,
      marketDividendYieldP75,
      industryDividendYield,
      dividendYieldFuture,
      dividendYieldFuture3Y,
      dividendYieldFormatted,
      marketDividendYieldP25Formatted,
      marketDividendYieldP75Formatted,
      industryDividendYieldFormatted,
      dividendYieldFutureFormatted,
      dividendYieldFuture3YFormatted,
      currentDividendPayout,
      currentDividendPayoutRatio: payoutRatio,
      currentDividendPayoutRatioFormatted,
      futureDividendPayout,
      futureDividendPayoutRatio: futurePayoutRatio,
      futureDividendPayoutRatioFormatted,
      cashDividendPayout,
      cashDividendPayoutRatio: cashPayoutRatio,
      cashDividendPayoutRatioFormatted,
      lastPaymentDividendCurrency,
      tradingItemCurrencyISO,
      buybackYieldFormatted,
      totalShareholderYieldFormatted,
      dividendPaymentsGrowthAnnualFormatted,
    };
  }
);

export const currentPayoutChartOptions = createSelector(
  [getAnalysis],
  (analysis) => {
    const payoutRatio = analysis.payoutRatio;
    const currentDividendPayoutRatioFormatted = formatter(payoutRatio, 0);
    const currentDividendPayout: PayoutEntity[] = [
      {
        label: 'paid',
        value: toSignificantNumber(payoutRatio < 0 ? 1 : payoutRatio, 0),
      },
    ];
    if (payoutRatio < 1 && payoutRatio >= 0) {
      currentDividendPayout.push({
        label: 'retained',
        value: toSignificantNumber(1 - payoutRatio, 0),
      });
    }
    return {
      data: currentDividendPayout,
      payoutRatio: payoutRatio,
      payoutRatioFormatted: currentDividendPayoutRatioFormatted,
    };
  }
);
